import {
  APIProvider,
  Map,
  MapCameraChangedEvent,
} from '@vis.gl/react-google-maps';
import classNames from 'classnames/bind';
import debounce from 'lodash/debounce';
import sortBy from 'lodash/sortBy';
import React, { useEffect, useMemo, useRef } from 'react';

import Loading from 'common/loading';
import config from 'config/index';
import { MinPriceFlight } from 'types/apollo';
import AirportMarker from './AirportMarker';
import CurrentMarker from './CurrentMarker';
import MapController from './MapController';
import MapFitBounder from './MapFitBounder';
import styles from './index.module.scss';

const cx = classNames.bind(styles);

interface GoogleMapProps {
  isLoading: boolean;
  isMapView: boolean;
  list: MinPriceFlight[];
  initBoundsItems: MinPriceFlight[];
  markerClickCode?: string;
  autoSort?: boolean;
  isResetRef?: React.MutableRefObject<boolean>;
  boundsChangedHandler(bounds: google.maps.LatLngBoundsLiteral): void;
  markerClickHandler(e: any, marker: MinPriceFlight): void;
  onReset: () => void;
}

export const MIN_ZOOM_LEVEL = 2;
export const MAX_ZOOM_LEVEL = 8;
export const DEFAULT_ZOOM_LEVEL = 3.3;

const PC_WIDTH = 1090;

const DEBOUNCE_WAIT = 500;
const MARKER_LIMIT = 100;

const DEFAULT_BACKGROUND = '#8ac6f4';

const defaultCenter = { lat: 0, lng: 0 };

const limitRestriction = {
  latLngBounds: {
    north: 87,
    south: -87,
    west: -Infinity,
    east: Infinity,
  },
};

const GoogleMap: React.FC<GoogleMapProps> = ({
  onReset,
  boundsChangedHandler,
  markerClickHandler,
  isLoading,
  list,
  isMapView,
  markerClickCode,
  initBoundsItems,
  isResetRef,
}) => {
  // 반응형 디자인이 viewport 기반으로 계산되기 때문에 ua가 아닌 viewport로 계산
  const deviceRef = useRef<'pc' | 'mo' | null>(null);

  // geojson 구조로, 추후 cluster 사용을 위해 데이터 구조 미리 적용
  const airportGeoLocations = useMemo(() => {
    const filteredList = list.filter(item => item.arrivalLocation.cityName);
    const sortedList = sortBy(
      filteredList,
      item => item.arrivalLocation.popularity,
    );

    return {
      type: 'FeatureCollection',
      features: sortedList.slice(0, MARKER_LIMIT).map(item => ({
        type: 'Feature',
        id: `${item.arrivalLocation.iataCode}_${item.duration}_${item.minPrice}`,
        geometry: {
          type: 'Point',
          coordinates: [
            item.arrivalLocation.location!.longitude,
            item.arrivalLocation.location!.latitude,
          ],
        },
        properties: item,
      })),
    };
  }, [list]);

  const currentLocation = list[0]?.departureLocation.location;

  const handleChangedBounds = debounce((e: MapCameraChangedEvent) => {
    if (isMapView || deviceRef.current === 'pc') {
      boundsChangedHandler(e.detail.bounds);
    }
  }, DEBOUNCE_WAIT);

  useEffect(() => {
    const deviceSetter = (width: number) => {
      return width < PC_WIDTH ? 'mo' : 'pc';
    };
    deviceRef.current = deviceSetter(window.innerWidth);

    const resizeHandler = debounce(() => {
      deviceRef.current = deviceSetter(window.innerWidth);
    }, DEBOUNCE_WAIT);

    window.addEventListener('resize', resizeHandler);

    return () => {
      window.removeEventListener('resize', resizeHandler);
    };
  }, []);

  return (
    <APIProvider
      apiKey={config.googleMap.apiKey}
      channel={config.googleMap.channel as number}
      language="ko"
      region="KR"
    >
      <Map
        mapId={config.googleMap.mapId}
        maxZoom={MAX_ZOOM_LEVEL}
        minZoom={MIN_ZOOM_LEVEL}
        defaultZoom={DEFAULT_ZOOM_LEVEL}
        defaultCenter={defaultCenter}
        gestureHandling={'greedy'}
        mapTypeId={'roadmap'}
        disableDefaultUI
        onBoundsChanged={handleChangedBounds}
        backgroundColor={DEFAULT_BACKGROUND}
        restriction={limitRestriction}
        className={cx('Map')}
        data-event-nlog="click"
        data-event-area-code="rcm.mapexlore"
      >
        <MapController
          onRetry={!isLoading && !list.length ? onReset : undefined}
        />
        {currentLocation && <CurrentMarker location={currentLocation} />}
        {airportGeoLocations.features.map((feature, i) => {
          const [lng, lat] = feature.geometry.coordinates;

          return (
            <AirportMarker
              key={feature.id}
              lng={lng}
              lat={lat}
              property={feature.properties}
              featureId={feature.id}
              onClick={markerClickHandler}
              clickCode={markerClickCode}
              currentLocation={currentLocation!}
            />
          );
        })}
        <MapFitBounder
          geoLocations={airportGeoLocations}
          customMobileFitBound
          initBoundsItems={initBoundsItems}
          isResetRef={isResetRef}
        />
        {isLoading && <Loading isCover />}
      </Map>
    </APIProvider>
  );
};

export default GoogleMap;
